import type { QueryClient } from '@tanstack/react-query';
import {
  useCollaborationHighlightStore,
  type CollaborationHighlightContext,
  type CollaborationHighlightEvent,
  type CollaborationHighlightTargetType,
  type CollaborationHighlightType,
} from '@/stores/collaborationHighlightStore';
import type { RealtimeContext, RealtimeEvent } from '@/types/realtime';
import { isRealtimeCollaborationProject } from '@/lib/realtime-collaboration-project';
import { applyStoryboardSoundEffectDeletedCacheSync } from '@/lib/realtime/storyboard-sound-effect-cache-sync';
import { applyStoryboardVoiceoverDeletedCacheSync } from '@/lib/realtime/storyboard-voiceover-cache-sync';
import {
  applyStoryboardVideoDeletedCacheSync,
  refreshStoryboardVideoCollaborationCache,
} from '@/lib/realtime/storyboard-video-cache-sync';
import {
  canShowHighlightOnRoute,
  normalizeHighlightContextForRoute,
  resolveCanvasRouteProjectId,
} from '@/lib/collaboration/highlight-route-compat';

type RawHighlightTarget = {
  targetType?: unknown;
  targetId?: unknown;
  targetProjectId?: unknown;
  targetStoryboardId?: unknown;
  highlightType?: unknown;
  elementType?: unknown;
  changeType?: unknown;
};

const TARGET_TYPES = new Set<CollaborationHighlightTargetType>([
  'storyboard',
  'storyboard_item',
  'resource_element',
  'resource_tag',
  'project_resource',
  'dialogue',
  'storyboard_image',
  'storyboard_video',
  'voiceover',
  'sound_effect',
  'review_diff',
  'screenplay_scene',
  'parse_flow_storyboard',
  'resource_pin',
  'temporary_video',
]);

const HIGHLIGHT_TYPES = new Set<CollaborationHighlightType>([
  'new',
  'updated',
  'attention',
  'ready',
  'removed',
]);

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function readStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0);
}

function readHighlightContext(value: unknown): CollaborationHighlightContext | null {
  return value === 'project_page' || value === 'parse_flow' || value === 'canvas' ? value : null;
}

function readTargetType(value: unknown): CollaborationHighlightTargetType | null {
  return typeof value === 'string' && TARGET_TYPES.has(value as CollaborationHighlightTargetType)
    ? (value as CollaborationHighlightTargetType)
    : null;
}

function readHighlightType(value: unknown): CollaborationHighlightType {
  return typeof value === 'string' && HIGHLIGHT_TYPES.has(value as CollaborationHighlightType)
    ? (value as CollaborationHighlightType)
    : 'updated';
}

const RESOURCE_TYPE_TO_ELEMENT_TYPE: Record<string, string> = {
  character: 'character',
  scene: 'location',
  prop: 'prop',
  footage: 'footage',
};

function inferElementType(payload: Record<string, unknown>): string | null {
  const elementType = readString(payload.elementType);
  if (elementType) return elementType;
  const resourceType = readString(payload.resourceType);
  if (!resourceType) return null;
  return RESOURCE_TYPE_TO_ELEMENT_TYPE[resourceType] ?? resourceType;
}

function getRouteContext(context: RealtimeContext): CollaborationHighlightContext | null {
  if (context.pathname === '/project/parse-flow') return 'parse_flow';
  if (/^\/project\/[^/]+\/canvas$/u.test(context.pathname)) return 'canvas';
  if (/^\/project\/[^/]+$/u.test(context.pathname)) return 'project_page';
  return null;
}

function getRouteProjectId(context: RealtimeContext): string | null {
  if (context.pathname === '/project/parse-flow') {
    return new URLSearchParams(context.search).get('parentProjectId') ?? context.currentProjectId;
  }
  return resolveCanvasRouteProjectId(context.pathname, context.currentProjectId);
}

function readActor(payload: Record<string, unknown>): CollaborationHighlightEvent['actor'] {
  const actor = payload.actor;
  if (actor && typeof actor === 'object' && !Array.isArray(actor)) {
    const actorRecord = actor as Record<string, unknown>;
    return {
      userId: readString(actorRecord.userId) ?? readString(payload.actorUserId) ?? '',
      displayName: readString(actorRecord.displayName),
    };
  }

  return {
    userId: readString(payload.actorUserId) ?? '',
    displayName: readString(payload.actorDisplayName),
  };
}

function buildHighlightEvent(
  realtimeEvent: RealtimeEvent,
  payload: Record<string, unknown>,
  target: RawHighlightTarget
): CollaborationHighlightEvent | null {
  const projectId = readString(payload.projectId);
  const context = readHighlightContext(payload.context);
  const targetType = readTargetType(target.targetType ?? payload.targetType);
  const targetId = readString(target.targetId ?? payload.targetId);
  if (!projectId || !context || !targetType || !targetId) return null;

  return {
    eventId:
      readString(payload.eventId) ??
      realtimeEvent.eventId ??
      `${projectId}:${context}:${targetType}:${targetId}:${readString(payload.occurredAt) ?? Date.now()}`,
    projectId,
    screenplayId: readString(payload.screenplayId),
    context,
    targetType,
    targetId,
    targetProjectId: readString(target.targetProjectId ?? payload.targetProjectId),
    affectedProjectIds: readStringArray(payload.affectedProjectIds),
    targetStoryboardId: readString(target.targetStoryboardId ?? payload.targetStoryboardId),
    elementType: readString(target.elementType ?? payload.elementType) ?? inferElementType(payload),
    changeType: readString(target.changeType ?? payload.changeType),
    highlightType: readHighlightType(target.highlightType ?? payload.highlightType),
    actor: readActor(payload),
    summary: readString(payload.summary),
    batchId: readString(payload.batchId),
    occurredAt: readString(payload.occurredAt) ?? new Date().toISOString(),
  };
}

function readHighlightEvents(event: RealtimeEvent): CollaborationHighlightEvent[] {
  const targets = Array.isArray(event.payload.targets) ? event.payload.targets : [event.payload];
  return targets
    .map((target) =>
      buildHighlightEvent(
        event,
        event.payload,
        target && typeof target === 'object' && !Array.isArray(target)
          ? (target as RawHighlightTarget)
          : event.payload
      )
    )
    .filter((item): item is CollaborationHighlightEvent => Boolean(item));
}

function invalidateHighlightQueries(
  queryClient: QueryClient,
  highlight: CollaborationHighlightEvent
): void {
  if (highlight.context === 'canvas') {
    void queryClient.invalidateQueries({ queryKey: ['canvas-layout'] });
    void queryClient.refetchQueries({ queryKey: ['canvas-layout'], type: 'active' });
  }

  if (highlight.targetType === 'storyboard_item') {
    if (highlight.targetStoryboardId) {
      void queryClient.invalidateQueries({
        queryKey: ['storyboard-items', highlight.targetStoryboardId],
      });
      void queryClient.refetchQueries({
        queryKey: ['storyboard-items', highlight.targetStoryboardId],
        type: 'active',
      });
    } else {
      void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
    }
    void queryClient.invalidateQueries({ queryKey: ['storyboardBoard'] });
    return;
  }

  if (highlight.targetType === 'storyboard_image') {
    if (highlight.targetStoryboardId) {
      void queryClient.invalidateQueries({
        queryKey: ['storyboard-images', highlight.targetStoryboardId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['storyboard-plans', 'storyboard', highlight.targetStoryboardId, 1],
      });
    } else {
      void queryClient.invalidateQueries({ queryKey: ['storyboard-images'] });
    }
    return;
  }

  void queryClient.invalidateQueries({ queryKey: ['projects', 'detail'] });

  if (
    highlight.targetType === 'resource_element' ||
    highlight.targetType === 'resource_tag' ||
    highlight.targetType === 'project_resource'
  ) {
    void queryClient.invalidateQueries({ queryKey: ['resource-library', 'v1'] });
    void queryClient.invalidateQueries({ queryKey: ['project-elements'] });
    void queryClient.invalidateQueries({ queryKey: ['project-element-tags'] });
  }

  if (
    highlight.targetType === 'storyboard' ||
    highlight.targetType === 'dialogue' ||
    highlight.targetType === 'storyboard_video' ||
    highlight.targetType === 'voiceover' ||
    highlight.targetType === 'sound_effect' ||
    highlight.targetType === 'parse_flow_storyboard'
  ) {
    void queryClient.invalidateQueries({ queryKey: ['storyboards'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardBoard'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-items'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-images'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-videos'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboardDialogues'] });
    void queryClient.invalidateQueries({ queryKey: ['dialogueVoiceovers'] });
    void queryClient.invalidateQueries({ queryKey: ['storyboard-sound-effects'] });
    if (highlight.targetType === 'dialogue' && highlight.targetStoryboardId) {
      void queryClient.invalidateQueries({
        queryKey: ['storyboardDialogues', highlight.targetStoryboardId],
      });
      void queryClient.refetchQueries({
        queryKey: ['storyboardDialogues', highlight.targetStoryboardId],
        type: 'all',
      });
    }
    if (highlight.targetType === 'sound_effect') {
      if (highlight.targetStoryboardId) {
        void queryClient.invalidateQueries({
          queryKey: ['storyboard-sound-effects', highlight.targetStoryboardId],
        });
      }
      if (
        highlight.changeType === 'deleted' &&
        highlight.targetId &&
        highlight.targetStoryboardId
      ) {
        applyStoryboardSoundEffectDeletedCacheSync(queryClient, {
          storyboardId: highlight.targetStoryboardId,
          entityId: highlight.targetId,
        });
      }
    }
    if (highlight.targetType === 'voiceover') {
      if (highlight.targetStoryboardId) {
        void queryClient.invalidateQueries({
          queryKey: ['dialogueVoiceovers', 'storyboard', highlight.targetStoryboardId],
        });
      }
      if (
        (highlight.changeType === 'deleted' || highlight.highlightType === 'removed') &&
        highlight.targetId &&
        highlight.targetStoryboardId
      ) {
        applyStoryboardVoiceoverDeletedCacheSync(queryClient, {
          storyboardId: highlight.targetStoryboardId,
          entityId: highlight.targetId,
        });
      }
    }
    if (highlight.targetType === 'storyboard_video') {
      if (
        (highlight.changeType === 'deleted' || highlight.highlightType === 'removed') &&
        highlight.targetId &&
        highlight.targetStoryboardId
      ) {
        applyStoryboardVideoDeletedCacheSync(queryClient, {
          storyboardId: highlight.targetStoryboardId,
          entityId: highlight.targetId,
        });
      }
      void refreshStoryboardVideoCollaborationCache(queryClient, {
        storyboardId: highlight.targetStoryboardId,
        projectId: highlight.projectId,
        targetProjectId: highlight.targetProjectId,
        affectedProjectIds: highlight.affectedProjectIds,
      });
    }
  }

  if (highlight.targetType === 'screenplay_scene') {
    void queryClient.invalidateQueries({ queryKey: ['screenplays'] });
  }

  if (highlight.targetType === 'review_diff') {
    void queryClient.invalidateQueries({ queryKey: ['storyboard-reviews'] });
  }
}

const NON_HIGHLIGHT_CHANGE_TYPES = new Set([
  'deleted',
  'removed',
  'item_removed',
  'resource_removed',
  'tag_deleted',
  'association_removed',
]);

function shouldSuppressVisualHighlight(highlight: CollaborationHighlightEvent): boolean {
  return (
    highlight.highlightType === 'removed' ||
    Boolean(highlight.changeType && NON_HIGHLIGHT_CHANGE_TYPES.has(highlight.changeType))
  );
}

function isTagCreateResourceElementFanout(
  highlight: CollaborationHighlightEvent,
  payload: Record<string, unknown>
): boolean {
  return (
    highlight.targetType === 'resource_element' &&
    highlight.highlightType === 'new' &&
    readString(payload.source) === 'tag_create'
  );
}

function shouldStoreHighlight(
  highlight: CollaborationHighlightEvent,
  context: RealtimeContext,
  payload: Record<string, unknown>
): boolean {
  if (isTagCreateResourceElementFanout(highlight, payload)) return false;
  if (shouldSuppressVisualHighlight(highlight)) return false;
  if (highlight.highlightType !== 'new') return false;
  if (!context.currentProject || !isRealtimeCollaborationProject(context.currentProject))
    return false;
  const routeContext = getRouteContext(context);
  const routeProjectId = getRouteProjectId(context);
  const parentProjectId = context.currentProjectParentId ?? null;
  return Boolean(
    routeContext &&
    routeProjectId &&
    canShowHighlightOnRoute(highlight.context, routeContext) &&
    (highlight.projectId === routeProjectId ||
      highlight.targetProjectId === routeProjectId ||
      highlight.affectedProjectIds?.includes(routeProjectId) ||
      (parentProjectId != null && highlight.projectId === parentProjectId))
  );
}

export function handleProjectCollaborationRealtimeEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  context: RealtimeContext
): void {
  if (event.topic !== 'project_collaboration') return;

  const highlights = readHighlightEvents(event);
  for (const highlight of highlights) {
    invalidateHighlightQueries(queryClient, highlight);
    if (shouldStoreHighlight(highlight, context, event.payload)) {
      const routeContext = getRouteContext(context);
      if (!routeContext) continue;
      useCollaborationHighlightStore.getState().addHighlight({
        ...highlight,
        context: normalizeHighlightContextForRoute(highlight.context, routeContext),
      });
    }
  }
}
